export const SYSTOLIC_RANGE = { min: 70, max: 250 } as const;
export const DIASTOLIC_RANGE = { min: 40, max: 150 } as const;

export interface BpReading {
  status: "empty" | "invalid" | "ok";
  value?: number;
  error?: string;
}

export interface BpEvaluation {
  systolic: BpReading;
  diastolic: BpReading;
  /** Why Save is blocked, naming the field. Empty string when it can be saved. */
  blockedReason: string;
}

const WHOLE = /^\d+$/;

function read(label: "Systolic" | "Diastolic", text: string, range: { min: number; max: number }): BpReading {
  const raw = text.trim();
  if (raw === "") return { status: "empty" };
  if (!WHOLE.test(raw)) return { status: "invalid", error: `Enter ${label.toLowerCase()} as a whole number, in mmHg` };
  const value = Number(raw);
  if (value < range.min || value > range.max) {
    return { status: "invalid", value, error: `${label} ${raw} looks wrong \u2013 check the reading` };
  }
  return { status: "ok", value };
}

export function evaluateBp(systolicText: string, diastolicText: string): BpEvaluation {
  const systolic = read("Systolic", systolicText, SYSTOLIC_RANGE);
  let diastolic = read("Diastolic", diastolicText, DIASTOLIC_RANGE);
  if (systolic.status === "ok" && diastolic.status === "ok" && diastolic.value! >= systolic.value!) {
    diastolic = { status: "invalid", value: diastolic.value, error: "Diastolic must be lower than systolic \u2013 check the readings" };
  }
  const bad = [systolic.status === "invalid" ? "systolic" : "", diastolic.status === "invalid" ? "diastolic" : ""].filter(Boolean);
  const ready = systolic.status === "ok" && diastolic.status === "ok";
  const blockedReason = bad.length
    ? `Check the ${bad.join(" and ")} before continuing.`
    : !ready
      ? "Enter systolic and diastolic readings to continue."
      : "";
  return { systolic, diastolic, blockedReason };
}
